import { WorkItem } from "../types/workItems";
import { mapWorkItemToResidentData } from "./mdsMapping";

// NETSOL-12004 item #4: "Export to PDF for the Summary".
//
// The mds-queue widget renders inside a shadow DOM and the corporate npm
// registry blocks pulling a PDF library (jsPDF) — so instead of adding a
// dependency we render a plain, self-contained HTML document into a hidden
// iframe on the *top* document and invoke the browser's native print dialog,
// where the user picks "Save as PDF". No new dependency, no shadow-DOM caveats,
// and the output matches whatever the user currently has filtered on screen.

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Columns mirror the MDS table order (NETSOL-12004: ARD first).
const COLUMNS: { header: string; value: (item: WorkItem) => string }[] = [
  { header: "ARD (No later than)", value: (i) => mapWorkItemToResidentData(i).ardDate },
  { header: "Assessment Status", value: (i) => mapWorkItemToResidentData(i).statusNormalized },
  { header: "Resident Name", value: (i) => i.resident_name },
  { header: "Admission Date", value: (i) => i.admission_date || "N/A" },
  { header: "Assessment Type", value: (i) => i.assessment_name || i.assessment_type || "N/A" },
  { header: "Unsigned Sections", value: (i) => i.unsigned_sections || "N/A" },
  { header: "Due Date", value: (i) => i.due_date || "N/A" },
  { header: "CAA Completion", value: (i) => i.caa_completion_date || "N/A" },
  { header: "Care Plan Completion", value: (i) => i.care_plan_completion_date || "N/A" },
  { header: "Transmission Date", value: (i) => i.transmission_date || "N/A" },
];

function buildSummaryHtml(workItems: WorkItem[], title: string): string {
  const generatedAt = new Date().toLocaleString();
  const headerCells = COLUMNS.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");
  const rows = workItems
    .map((item) => {
      const cells = COLUMNS.map(
        (c) => `<td>${escapeHtml(c.value(item) ?? "N/A")}</td>`
      ).join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  const body =
    workItems.length > 0
      ? `<table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>`
      : `<p class="empty">No MDS Assessments to export.</p>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 12px; color: #555; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
  thead th { background: #f0f0f0; }
  tbody tr:nth-child(even) { background: #fafafa; }
  .empty { font-size: 13px; color: #555; }
  @media print { body { margin: 12mm; } }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">${workItems.length} assessment${workItems.length === 1 ? "" : "s"} &middot; Generated ${escapeHtml(generatedAt)}</p>
  ${body}
</body>
</html>`;
}

/**
 * Render the current MDS assessment summary into a hidden iframe and open the
 * browser print dialog (Save as PDF). Returns false when the environment has no
 * DOM (e.g. SSR) so callers can no-op safely.
 */
export function exportMdsSummaryToPdf(
  workItems: WorkItem[],
  title = "MDS Assessments Summary"
): boolean {
  if (typeof document === "undefined") return false;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return false;
  }

  doc.open();
  doc.write(buildSummaryHtml(workItems, title));
  doc.close();

  const win = iframe.contentWindow;
  if (!win) {
    document.body.removeChild(iframe);
    return false;
  }

  // Clean up the iframe once the print dialog is dismissed. `afterprint` fires
  // on the iframe's own window in modern browsers; a timeout backstops it.
  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  win.addEventListener("afterprint", cleanup);

  // Focus is required for print() in some browsers (Safari/Firefox).
  win.focus();
  win.print();

  // Backstop cleanup in case afterprint never fires (older Safari).
  setTimeout(cleanup, 60_000);

  return true;
}
